/**
 * Pure ranking helpers for the opener table's 'startTime' and 'event' sorts.
 * Extracted from `EntryTabByPath` so the ordering logic is unit-testable
 * without a live store. Both operate WITHIN the market-data-enabled tier — the
 * table's tier-first gate runs before these are called, so they never reorder
 * across the enabled/disabled boundary.
 */

/**
 * Concise browser-local label for an event start time: `Hoy HH:mm` when it
 * falls on the same calendar day as `now`, else `MMM d HH:mm`. Used for the
 * start-time line under the `⇈ >$1` pill (before the match goes LIVE).
 */
export function formatEventStart(ms: number, now: number): string {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const time = `${hh}:${mm}`;
  const n = new Date(now);
  const sameDay =
    d.getFullYear() === n.getFullYear() &&
    d.getMonth() === n.getMonth() &&
    d.getDate() === n.getDate();
  if (sameDay) return `Hoy ${time}`;
  const month = d.toLocaleString(undefined, { month: 'short' });
  return `${month} ${d.getDate()} ${time}`;
}

export interface StartSortRow {
  /** Event start (epoch-ms), or null for a non-match row. */
  startMs: number | null;
  /** Pair name — the stable tie-break. */
  name: string;
}

/**
 * Start-time order: already-LIVE rows first, then soonest upcoming, then rows
 * with no start (non-match) last — regardless of direction. `dir` flips only
 * the live/upcoming ordering; the nulls-last rule and the A→Z name tie-break
 * stay fixed. `nowMs` is the shared clock, so a row crossing its start moves
 * into the LIVE bucket on the next tick.
 */
export function compareByStartTime(
  a: StartSortRow,
  b: StartSortRow,
  nowMs: number,
  dir: 'asc' | 'desc',
): number {
  const am = a.startMs;
  const bm = b.startMs;
  if (am == null || bm == null) {
    if (am == null && bm == null) return a.name.localeCompare(b.name);
    return am == null ? 1 : -1;
  }
  const liveA = nowMs >= am;
  const liveB = nowMs >= bm;
  let cmp = 0;
  if (liveA !== liveB) cmp = liveA ? -1 : 1;
  else if (am !== bm) cmp = am - bm;
  if (cmp !== 0) return dir === 'asc' ? cmp : -cmp;
  return a.name.localeCompare(b.name);
}

export interface EventSortRow {
  /**
   * The GROUP's canonical start (epoch-ms) — the SAME value for every row of an
   * `eventKey`, NOT the row's own start. The caller must pass a per-group start
   * (e.g. the earliest across the group); otherwise cross-group ordering is not
   * a total order and `Array.sort` can split a match's markets apart. null when
   * the group has no usable start.
   */
  startMs: number | null;
  /** Stable per-match grouping key — identical for every market of a match. */
  eventKey: string;
  /** Row name — the within-group tie-break. */
  name: string;
}

/**
 * Earliest start (epoch-ms) per event key — the GROUP's canonical sort rank fed
 * to `compareByEvent`. Rows with an empty key or no parseable start are skipped.
 * Feed the result's `.get(eventKey)` as `EventSortRow.startMs` so every row of a
 * group shares one start and the ordering stays a total order.
 */
export function buildEventGroupStart(
  rows: Array<{ eventKey: string; startMs: number | null }>,
): Map<string, number> {
  const map = new Map<string, number>();
  for (const { eventKey, startMs } of rows) {
    if (!eventKey || startMs == null) continue;
    const cur = map.get(eventKey);
    if (cur == null || startMs < cur) map.set(eventKey, startMs);
  }
  return map;
}

/**
 * Event grouping: every market of one match (`eventKey`) contiguous, with the
 * groups themselves ordered by their canonical start time — `dir === 'asc'`
 * puts LIVE/soonest groups first (like the startTime sort ascending), `'desc'`
 * reverses. null-start groups and empty-key rows always sink, regardless of
 * `dir`. Within a group, A→Z by name (fixed). Contiguity holds ONLY when
 * `startMs` is a per-group value (identical for every row sharing an
 * `eventKey`) — see `startMs` above.
 */
export function compareByEvent(
  a: EventSortRow,
  b: EventSortRow,
  nowMs: number,
  dir: 'asc' | 'desc',
): number {
  if (a.eventKey !== b.eventKey) {
    const am = a.startMs;
    const bm = b.startMs;
    if (am == null || bm == null) {
      // null-start groups last in BOTH directions.
      if (!(am == null && bm == null)) return am == null ? 1 : -1;
    } else {
      const liveA = nowMs >= am;
      const liveB = nowMs >= bm;
      let cmp = 0;
      if (liveA !== liveB) cmp = liveA ? -1 : 1;
      else if (am !== bm) cmp = am - bm;
      if (cmp !== 0) return dir === 'asc' ? cmp : -cmp;
    }
    // Tie on start (or both null): empty-key rows sink, else stable by key.
    if (!a.eventKey) return 1;
    if (!b.eventKey) return -1;
    return a.eventKey.localeCompare(b.eventKey);
  }
  return a.name.localeCompare(b.name);
}
